import { CheckCircleFilled, CheckOutlined, Loading3QuartersOutlined, PlusOutlined } from '@ant-design/icons'
import { type HierarchyData, HierarchySelectEditor } from '@components/_SiteWide/Grid/CellEditors/HierarchySelectEditor'
import { InNetworkTreeSelectEditor } from '@components/_SiteWide/Grid/CellEditors/InNetworkTerminalsTreeSelect'
import { SiteMarketHierarchyRenderer } from '@components/_SiteWide/Grid/CellRenderers/SiteMarketHierarchyRenderer'
import { TerminalHierarchyRenderer } from '@components/_SiteWide/Grid/CellRenderers/TerminalHierarchyRenderer'
import { InfoIcon } from '@components/_SiteWide/InfoIcon'
import type { FormConfiguration } from '@components/DriverManagement/DriverForms/api/schema'
import { BBDTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Popover, Switch } from 'antd'
import React from 'react'

function StatusRenderer({ data, handleStatusToggle }) {
  return (
    <Switch
      style={{ width: '100px' }}
      className='grid-toggle float-right'
      onClick={() => handleStatusToggle(data)}
      checkedChildren={
        <span>
          <CheckOutlined className='switch-icon-fix' />
          <span className='switch-text-fix'>Active</span>
        </span>
      }
      unCheckedChildren={<span>Inactive</span>}
      checked={data.enabled}
    />
  )
}

const formNameRenderer = (name: string, description: string) => {
  return (
    <Horizontal verticalCenter style={{ gap: '.3rem' }}>
      <Texto>{name}</Texto>
      <InfoIcon tooltipTitle={description} />
    </Horizontal>
  )
}

export const getColumnDefs = (
  marketHierarchy: HierarchyData[],
  supplyZoneHierarchy: HierarchyData[],
  handleStatusToggle: (form: FormConfiguration) => Promise<void>
) => {
  return [
    {
      headerName: 'Form Name',
      field: 'name',
      enableRowGroup: false,
      cellRenderer: (params) => formNameRenderer(params?.data.name, params?.data.description),
    },
    {
      headerName: 'Status',
      headerTooltip: 'Inactive forms will not be accessible to drivers.',
      enableRowGroup: false,
      field: 'enabled',
      cellRenderer: (params) => <StatusRenderer data={params?.data} handleStatusToggle={handleStatusToggle} />,
    },
    {
      headerName: 'Required Sites',
      headerTooltip: 'All active forms will be accessible at sites but must be completed at required sites.',
      enableRowGroup: false,
      field: 'site_ids',
      cellEditor: HierarchySelectEditor,
      cellEditorPopup: true,
      suppressMenu: true,
      cellEditorParams: {
        treeData: marketHierarchy,
        width: 400,
        propOverrides: {
          placeholder: 'Select Required Sites',
        },
      },
      cellRenderer: (params) => {
        const marketNames = params?.data.markets.map((market) => market.name)
        const sectorNames = params?.data.sectors.map((sector) => sector.name)
        const siteNumbers = params?.data.sites.map((site) => site.name)
        return (
          <SiteMarketHierarchyRenderer
            marketNames={marketNames}
            sectorNames={sectorNames}
            siteNumbers={siteNumbers}
            totalCount={params?.data.site_ids.length}
          />
        )
      },
    },
    {
      headerName: 'Required Terminals',
      headerTooltip: 'All active forms will be accessible at terminals but must be completed at required terminals.',
      field: 'terminal_ids',
      editable: true,
      suppressMenu: true,
      cellEditor: HierarchySelectEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        treeData: supplyZoneHierarchy,
        width: 400,
        propOverrides: {
          placeholder: 'Select Required Terminals',
        },
      },
      cellRenderer: (params) => {
        const terminalNames = params?.data.terminals.map((terminal) => terminal.name)
        const supplyZones = params?.data.supply_zones.map((zone) => zone.name)
        return (
          <TerminalHierarchyRenderer
            terminalNames={terminalNames}
            supplyZones={supplyZones}
            totalCount={params?.data.terminal_ids.length}
          />
        )
      },
    },
  ]
}
